import { useEffect } from 'react';

import classNames from 'classnames';

import { ExternalLink } from '@uikit/components/ExternalLink';

import type { RewardPool } from '@shared/api/types/rewardPools';
import { useAuth } from '@shared/common/providers/AuthProvider';

import { MarkCheck } from '@ui/uikit/components/icons/mono/MarkCheck';

import { useRequirements } from '../../model/useRequirements';
import { ActionsList } from './ActionsList/ActionsList';
import { VerifyRequirementButton } from './buttons/VerifyRequirementButton/VerifyRequirementButton';
import { List } from './List/List';
import { ListItem } from './List/ListItem';
import { ListItemButton } from './List/ListItemButton';
import { ListWrapper } from './List/ListWrapper';

type Props = {
  rewardPool: RewardPool;
  hasBorders?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
};

export const RequirementsList = ({
  rewardPool,
  showHeader = true,
  showFooter = true,
  hasBorders = true,
}: Props) => {
  const {
    requirements,
    hasRequirements,
    qualifiedRequirementsCount,
    qualifiedAllRequirements,
    qualifiedEnoughRequirements,
    query,
  } = useRequirements(rewardPool);

  const { refetch } = query;

  const { isAuth } = useAuth();

  useEffect(() => {
    void refetch();
  }, [isAuth, refetch]);

  return (
    <>
      <ListWrapper
        header={
          showHeader && (
            <div className="flex xl:justify-between max-xl:flex-col gap-4 xl:gap-6">
              <h2 className="heading-h2-adaptive">Pool Requirements</h2>

              <span className="chip chip-lg min-h-10 font-semibold max-md:w-full md:max-w-[22.5rem] xl:max-w-none">
                <MarkCheck
                  className={classNames('chip-icon', {
                    'text-base-text-green': qualifiedAllRequirements,
                    'text-base-text-quaternary': !qualifiedAllRequirements,
                  })}
                />
                {qualifiedRequirementsCount} / {requirements.length} Requirements met
              </span>
            </div>
          )
        }
        showFooter={showFooter}
      >
        <List
          isEmpty={!hasRequirements}
          query={query}
          hasBorders={hasBorders}
          data-qa="requirements_list"
        >
          <>
            {requirements.map((requirement, index) => {
              return (
                <ListItem
                  key={index}
                  data-qa={`requirement-id_${requirement.id}`}
                  isDisabled={requirement.qualified}
                  label={requirement.title}
                  description={requirement.description}
                  buttons={
                    <>
                      {requirement.action && !requirement.qualified && (
                        <ListItemButton variant="secondary">
                          <ExternalLink href={requirement.action.href} className="w-full">
                            {requirement.action.label}
                          </ExternalLink>
                        </ListItemButton>
                      )}
                      <VerifyRequirementButton requirement={requirement} refetch={refetch} />
                    </>
                  }
                />
              );
            })}
            <ActionsList rewardPool={rewardPool} locked={!qualifiedEnoughRequirements} />
          </>
        </List>
      </ListWrapper>
    </>
  );
};
